import { useState } from 'react';
import { Input } from '@/components/forms';
import { Button } from '@/components/ui';
import { useCreateDeal, useDealClients } from '@/api/useDeals';
import { useModalStore } from '@/stores';
import styles from './QuickCreateDealModal.module.css';

interface QuickCreateDealModalProps {
  initialDate?: string;
}

export const QuickCreateDealModal = ({ initialDate }: QuickCreateDealModalProps) => {
  const [clientName, setClientName] = useState('');
  const [info, setInfo] = useState('');
  const [eventDate, setEventDate] = useState(initialDate || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const createDeal = useCreateDeal();
  const { closeModal } = useModalStore();
  const { data: suggestions = [] } = useDealClients(clientName);

  const handleCreate = () => {
    if (!clientName.trim()) return;
    createDeal.mutate(
      {
        title: clientName.trim(),
        client_name: clientName.trim(),
        notes: info.trim(),
        event_date: eventDate || null,
      },
      { onSuccess: () => closeModal() },
    );
  };

  const handleClientChange = (val: string) => {
    setClientName(val);
    setShowSuggestions(true);
  };

  const pickSuggestion = (name: string) => {
    setClientName(name);
    setShowSuggestions(false);
  };

  const filtered = suggestions.filter((s) => s.toLowerCase() !== clientName.toLowerCase());

  return (
    <div className={styles.modal}>
      <h2 className={styles.title}>New Deal</h2>
      <div className={styles.fields}>
        <div className={styles.clientField}>
          <Input
            name="client_name"
            label="Company"
            value={clientName}
            setValue={handleClientChange}
            placeholder="Company name"
          />
          {showSuggestions && filtered.length > 0 && clientName.length >= 1 && (
            <div className={styles.suggestions}>
              {filtered.map((name) => (
                <button
                  key={name}
                  className={styles.suggestion}
                  onMouseDown={() => pickSuggestion(name)}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>
        <Input
          name="info"
          label="Information"
          value={info}
          setValue={setInfo}
          multiline
          rows={5}
          placeholder="Deal details, event info, requirements..."
        />
        <Input
          name="event_date"
          label="Event Date"
          type="date"
          value={eventDate}
          setValue={setEventDate}
        />
      </div>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleCreate}
          disabled={!clientName.trim() || createDeal.isPending}
        >
          {createDeal.isPending ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </div>
  );
};
